import { Component, computed, inject, input } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ChannelLink, CutMedium } from '../shows.model';
import { embedUrl } from '../shows-player';

/**
 * One recording, embedded from the channel that plays it. Changing `startAt` reloads the player
 * at that point — how a chapter is opened (see `shows-player.ts`). Video keeps a 16:9 frame;
 * audio a short strip.
 */
@Component({
  selector: 'multisite-show-player',
  template: `
    @if (src(); as source) {
      @if (medium() === 'audio') {
        <iframe [src]="source" [title]="title()" style="width: 100%; height: 200px; border: 0" allow="autoplay" loading="lazy"></iframe>
      } @else {
        <div style="padding: 56.25% 0 0 0; position: relative">
          <iframe
            [src]="source"
            [title]="title()"
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: 0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowfullscreen></iframe>
        </div>
      }
    }
  `,
})
export class ShowPlayerComponent {
  private sanitizer = inject(DomSanitizer);

  link = input.required<ChannelLink>();
  medium = input<CutMedium>('video');
  /** Seconds into the recording; null for the start. */
  startAt = input<number | null>(null);
  /** Play at once: after a reader chose a chapter, never on arrival. */
  autoplay = input(false);
  title = input('');

  /** The raw address, for tests and for the page's own checks. */
  url = computed(() => embedUrl(this.link(), this.startAt(), this.autoplay()));

  src = computed<SafeResourceUrl | null>(() => {
    const url = this.url();

    return url === null ? null : this.sanitizer.bypassSecurityTrustResourceUrl(url);
  });
}
